<script setup lang="ts">
/**
 * ImageLightbox —— 富文本图片全屏查看灯箱（F25）。
 *
 * Teleport 到 body 的模态层：深色遮罩 + 居中图片 + 图像正下方固定操作栏
 * （放大/缩小/左旋/右旋/重置/关闭 + 缩放百分比）。变换状态全部委托给
 * `useImageViewerTransform`，本组件只负责事件接线：ESC / 遮罩点击关闭、
 * body 滚动锁定、滚轮缩放、pointer 拖拽平移（任意缩放级别可用）。
 */
import { onBeforeUnmount, onMounted, ref } from 'vue';

import Icon from '@/components/Icon.vue';
import { useAssetFallback } from '@/composables/use-asset-fallback';
import {
  MAX_SCALE,
  MIN_SCALE,
  useImageViewerTransform,
} from '@/composables/use-image-viewer-transform';
import { copy } from '@/i18n/zh-CN';

const props = defineProps<{
  readonly src: string;
  readonly alt?: string;
}>();
const emit = defineEmits<{ close: [] }>();

const viewer = useImageViewerTransform();
const { failed: imageFailed, handleError: handleImageError } = useAssetFallback(
  `lightbox src="${props.src}"`,
);

const overlayRef = ref<HTMLElement | null>(null);
const dragging = ref(false);
let lastPointerX = 0;
let lastPointerY = 0;
let previousBodyOverflow = '';

function close(): void {
  emit('close');
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    close();
  }
}

function onWheel(event: WheelEvent): void {
  if (event.deltaY < 0) {
    viewer.zoomIn();
    return;
  }
  viewer.zoomOut();
}

/** 任意缩放级别都可拖拽平移（甲方反馈：缩小到 1x 以下也不应锁死拖拽）。 */
function onPointerDown(event: PointerEvent): void {
  dragging.value = true;
  lastPointerX = event.clientX;
  lastPointerY = event.clientY;
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
}

function onPointerMove(event: PointerEvent): void {
  if (!dragging.value) {
    return;
  }
  viewer.panBy(event.clientX - lastPointerX, event.clientY - lastPointerY);
  lastPointerX = event.clientX;
  lastPointerY = event.clientY;
}

function onPointerUp(): void {
  dragging.value = false;
}

onMounted(() => {
  previousBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  window.addEventListener('keydown', onKeydown);
  overlayRef.value?.focus();
});

onBeforeUnmount(() => {
  document.body.style.overflow = previousBodyOverflow;
  window.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div
      ref="overlayRef"
      :class="$style.overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="copy.lightbox.dialogAria"
      tabindex="-1"
      data-testid="lightbox-overlay"
      @click.self="close"
      @wheel.prevent="onWheel"
    >
      <button
        type="button"
        :class="$style.closeButton"
        :aria-label="copy.lightbox.close"
        data-action="close"
        @click="close"
      >
        <Icon name="close" :size="20" />
      </button>

      <div :class="$style.stage">
        <div :class="$style.imageArea">
          <p v-if="imageFailed" :class="$style.error" role="status">
            {{ copy.fallback.asset.imageFailed }}
          </p>
          <img
            v-else
            :class="[$style.image, dragging ? $style.imageDragging : null]"
            :src="props.src"
            :alt="props.alt ?? ''"
            :style="{ transform: viewer.transformStyle.value }"
            draggable="false"
            data-testid="lightbox-image"
            @error="handleImageError"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerUp"
          />
        </div>

        <div :class="$style.toolbar" data-testid="lightbox-toolbar">
          <button
            type="button"
            :class="$style.toolButton"
            :aria-label="copy.lightbox.zoomOut"
            :disabled="viewer.scale.value <= MIN_SCALE"
            data-action="zoom-out"
            @click="viewer.zoomOut"
          >
            <Icon name="zoom-out" :size="18" />
          </button>
          <span :class="$style.scaleLabel" data-testid="lightbox-scale">
            {{ viewer.scaleLabel.value }}
          </span>
          <button
            type="button"
            :class="$style.toolButton"
            :aria-label="copy.lightbox.zoomIn"
            :disabled="viewer.scale.value >= MAX_SCALE"
            data-action="zoom-in"
            @click="viewer.zoomIn"
          >
            <Icon name="zoom-in" :size="18" />
          </button>
          <button
            type="button"
            :class="$style.toolButton"
            :aria-label="copy.lightbox.rotateLeft"
            data-action="rotate-left"
            @click="viewer.rotateLeft"
          >
            <Icon name="rotate-left" :size="18" />
          </button>
          <button
            type="button"
            :class="$style.toolButton"
            :aria-label="copy.lightbox.rotateRight"
            data-action="rotate-right"
            @click="viewer.rotateRight"
          >
            <Icon name="rotate-right" :size="18" />
          </button>
          <button
            type="button"
            :class="$style.toolButton"
            :aria-label="copy.lightbox.reset"
            data-action="reset"
            @click="viewer.reset"
          >
            <Icon name="reset" :size="18" />
          </button>
          <button
            type="button"
            :class="$style.toolButton"
            :aria-label="copy.lightbox.close"
            data-action="close"
            @click="close"
          >
            <Icon name="close" :size="18" />
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style module>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--lightbox-overlay-bg);
  outline: none;
}

.closeButton {
  position: absolute;
  top: var(--space-2);
  right: var(--space-2);
  width: var(--lightbox-close-size);
  height: var(--lightbox-close-size);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--radius-sm);
  background: var(--lightbox-toolbar-bg);
  color: var(--lightbox-toolbar-text);
}

.closeButton:hover,
.closeButton:focus-visible {
  background: var(--lightbox-toolbar-hover-bg);
}

.stage {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: var(--lightbox-view-max-width);
  max-height: 92vh;
  gap: var(--space-2);
}

.imageArea {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  max-width: var(--lightbox-view-max-width);
  max-height: calc(var(--lightbox-view-max-height) - var(--lightbox-toolbar-height) - var(--space-2));
}

.image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  touch-action: none;
  cursor: grab;
  transition: transform 0.15s ease-out;
}

.imageDragging {
  cursor: grabbing;
  transition: none;
}

.error {
  margin: 0;
  padding: var(--space-3);
  color: var(--lightbox-toolbar-text);
  font-size: var(--font-size-base);
}

.toolbar {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  height: var(--lightbox-toolbar-height);
  padding: 0 var(--space-2);
  border-radius: var(--radius-md);
  background: var(--lightbox-toolbar-bg);
  color: var(--lightbox-toolbar-text);
}

.toolButton {
  width: var(--lightbox-close-size);
  height: var(--lightbox-close-size);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: inherit;
}

.toolButton:hover:not(:disabled),
.toolButton:focus-visible {
  background: var(--lightbox-toolbar-hover-bg);
}

.toolButton:disabled {
  opacity: 0.4;
  cursor: default;
}

.scaleLabel {
  min-width: 48px;
  text-align: center;
  font-size: var(--font-size-sm);
}
</style>
